import { END_IMPERSONATION_QUERY_PARAM, adminRoutes, adminUrl } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Badge, Button, Icon } from "@sagot/ui";
import { useClient, useSession } from "../session/session-provider";
import { useWorkspaceContext } from "../session/workspace-context";

/**
 * Shown while the console runs on an admin impersonation token (`imp` claim). "End session"
 * drops the token here and hands back to the admin app, which ends the session with staff
 * credentials (`POST /admin/v1/impersonation/{id}/end`).
 */
export function ImpersonationBanner() {
  const { t, i18n } = useTranslation();
  const session = useSession();
  const client = useClient();
  const { summary } = useWorkspaceContext();
  const claims = session.token?.claims;
  if (!claims?.imp) return null;
  const endsAt = session.token ? new Intl.DateTimeFormat(i18n.language === "fil" ? "fil-PH" : "en-PH", { timeStyle: "short" }).format(session.token.expiresAt) : "";
  const end = () => {
    const target = `${adminUrl(adminRoutes.tenants())}?${END_IMPERSONATION_QUERY_PARAM}=${encodeURIComponent(claims.imp ?? "")}`;
    client.session.clear({ broadcast: false });
    window.location.assign(target);
  };
  return (
    <div role="status" className="mb-6 flex flex-col gap-3 rounded-lg border border-line bg-warning-soft px-4 py-3 sm:flex-row sm:items-center">
      <Icon name="shield" className="size-5 shrink-0 text-warning" />
      <div className="min-w-0 flex-1 text-sm text-fg">
        <strong className="mr-1.5">{t("console.impersonationTitle")}</strong>
        {t("console.impersonationBody", { workspace: summary.name })}
        <span className="ml-2 inline-flex flex-wrap gap-1.5 align-middle">
          {claims.ro ? <Badge tone="warning">{t("console.impersonationReadOnly")}</Badge> : null}
          <Badge tone="neutral">{t("console.impersonationEnds", { time: endsAt })}</Badge>
        </span>
      </div>
      <Button variant="secondary" size="sm" onClick={end}>
        <Icon name="log-out" />
        {t("console.impersonationEnd")}
      </Button>
    </div>
  );
}
